import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, Save, ArrowRight, Lock } from "lucide-react";
import LeadForm from "@/components/energy/LeadForm";
import { ResultGrid, EstimateNote, WhatsAppCTA, sle } from "@/components/energy/tool";
import { api, authHeaders, getToken, formatApiError } from "@/lib/solarApi";

export default function PlanResults({ plan, items, profile }) {
  const navigate = useNavigate();
  const [downloading, setDownloading] = useState(false);
  const [assessment, setAssessment] = useState(null);
  const [leadMode, setLeadMode] = useState(null); // "assessment" | "quote" | null
  const [error, setError] = useState("");

  const payload = { profile, appliances: items, goal: plan.goal };
  const summaryCtx = {
    daily_kwh: plan.energy.daily_kwh,
    peak_w: plan.energy.peak_w,
    solar_kw: plan.solar.installed_kw,
    battery_kwh: plan.battery.installed_kwh,
    inverter_kw: plan.inverter.recommended_kw,
    total_sle: plan.costs.total_sle,
  };
  const waMessage = `Hello BNM Sustainable Energy Limited. I completed the BNM Energy Assessment. My results: ${plan.energy.daily_kwh} kWh/day, peak ${plan.energy.peak_w} W, recommended ${plan.solar.installed_kw} kW solar, ${plan.battery.installed_kwh} kWh battery, ${plan.inverter.recommended_kw} kW inverter. I would like assistance with my energy system.`;

  const downloadPdf = async () => {
    setDownloading(true);
    setError("");
    try {
      const res = await api.post("/energy/plan-pdf", payload, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "BNM-Preliminary-Energy-Plan.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(formatApiError(err, "Could not generate the PDF. Please try again."));
    } finally {
      setDownloading(false);
    }
  };

  const savePlan = async () => {
    if (!getToken()) {
      toast.error("Sign in to save your plan", { description: "Use the Solar Calculator sign-in, or continue without saving." });
      return;
    }
    try {
      await api.post("/energy/plans", payload, { headers: authHeaders() });
      toast.success("Plan saved to your dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const requestAssessment = async () => {
    if (!getToken()) {
      toast.error("Sign in to request a paid assessment", { description: "Create a free account in the Solar Calculator first." });
      return;
    }
    try {
      const { data } = await api.post("/energy/assessments", { plan, items, profile }, { headers: authHeaders() });
      setAssessment(data);
      toast.success("Assessment requested", { description: `Reference ${data.reference}` });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const sendToSolarCalculator = () => {
    localStorage.setItem("bnm_engine_prefill", JSON.stringify({ items, profile }));
    navigate("/solar/calculator");
  };

  return (
    <div data-testid="energy-plan-results" className="space-y-8">
      <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-emerald-700">BNM Preliminary Energy Plan · {plan.goal_label}</p>
        <h2 className="mt-2 font-display text-2xl font-extrabold text-slate-900">Your Energy System Recommendation</h2>
      </div>

      <ResultGrid items={[
        { label: "Daily consumption", value: `${plan.energy.daily_kwh} kWh`, hint: `Day ${plan.energy.day_kwh} · Night ${plan.energy.night_kwh} kWh` },
        { label: "Peak demand", value: `${plan.energy.peak_w} W`, hint: "Maximum power needed at once" },
        { label: "Surge demand", value: `${plan.energy.surge_w} W`, hint: "Motor/compressor start-up" },
        { label: "Monthly use", value: `${plan.energy.monthly_kwh} kWh`, hint: "Estimated 30-day total" },
      ]} />

      <ResultGrid items={[
        { label: "Solar PV", value: `${plan.solar.installed_kw} kW`, hint: `${plan.solar.panels} × ${plan.solar.panel_w} W panels · ~${plan.solar.daily_production_kwh} kWh/day` },
        { label: "Battery", value: `${plan.battery.installed_kwh} kWh`, hint: `${plan.battery.units} × ${plan.battery.unit_name} · ~${plan.battery.est_runtime_h} h night runtime` },
        { label: "Inverter", value: `${plan.inverter.recommended_kw} kW`, hint: `Pure sine wave hybrid · ${plan.inverter.system_voltage}` },
        { label: "Charge controller", value: `${plan.charge_controller.amps} A`, hint: `${plan.charge_controller.type} · ${plan.charge_controller.voltage}` },
      ]} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <h3 className="font-display text-base font-bold text-slate-900">Preliminary Cables & Protection</h3>
          <ul className="mt-4 space-y-2.5 text-sm text-slate-600">
            <li>Solar DC cable: <b className="text-slate-900">{plan.cables.solar.mm2} mm²</b> ({plan.cables.solar.current_a} A)</li>
            <li>Battery cable: <b className="text-slate-900">{plan.cables.battery.mm2} mm²</b> ({plan.cables.battery.current_a} A)</li>
            <li>AC cable: <b className="text-slate-900">{plan.cables.ac.mm2} mm²</b> ({plan.cables.ac.current_a} A)</li>
            <li>DC isolator {plan.protection.dc_isolator_a} A · Battery breaker {plan.protection.battery_breaker_a} A · AC breaker {plan.protection.ac_breaker_a} A</li>
            <li>{plan.protection.spd} · {plan.protection.earthing}</li>
          </ul>
          <p className="mt-4 text-[11px] text-slate-400">Preliminary engineering estimate — final cable sizing must be verified during site assessment.</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-6" data-testid="plan-costs">
          <h3 className="font-display text-base font-bold text-slate-900">Estimated Project Cost</h3>
          <ul className="mt-4 space-y-2.5 text-sm text-slate-600">
            <li className="flex justify-between"><span>Panels</span><b className="text-slate-900">{sle(plan.costs.panels_sle)}</b></li>
            <li className="flex justify-between"><span>Batteries</span><b className="text-slate-900">{sle(plan.costs.batteries_sle)}</b></li>
            <li className="flex justify-between"><span>Inverter</span><b className="text-slate-900">{sle(plan.costs.inverter_sle)}</b></li>
            <li className="flex justify-between"><span>Cables, protection, mounting</span><b className="text-slate-900">{sle(plan.costs.bos_sle)}</b></li>
            <li className="flex justify-between"><span>Installation</span><b className="text-slate-900">{sle(plan.costs.install_sle)}</b></li>
            <li className="flex justify-between border-t border-slate-200 pt-2.5">
              <span className="font-semibold text-slate-900">Estimated total</span>
              <b className="text-emerald-700" data-testid="plan-total-cost">{sle(plan.costs.total_sle)}</b>
            </li>
            {plan.costs.total_usd && <li className="flex justify-between text-xs text-slate-400"><span>Approx. USD</span><span>${plan.costs.total_usd.toLocaleString()}</span></li>}
          </ul>
          <p className="mt-4 text-[11px] text-slate-400">Illustrative estimate from configurable BNM price assumptions — not a quotation.</p>
        </div>
      </div>

      {plan.warnings.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-5 space-y-2">
          {plan.warnings.map((w, i) => <p key={i} className="text-sm text-amber-800 leading-relaxed">{w}</p>)}
        </div>
      )}

      <details className="rounded-lg border border-slate-200 bg-white p-5">
        <summary className="cursor-pointer font-display font-bold text-slate-900" data-testid="plan-assumptions">How we calculated this</summary>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
          {Object.entries(plan.assumptions).map(([k, v]) => (
            <div key={k} className="rounded-md bg-mist p-3">
              <p className="text-[11px] text-slate-500">{k.replace(/_/g, " ")}</p>
              <p className="mt-0.5 text-sm font-semibold text-slate-900">{typeof v === "number" && v < 1 ? `${Math.round(v * 100)}%` : v}</p>
            </div>
          ))}
        </div>
      </details>

      <EstimateNote />
      {error && <p role="alert" className="text-sm text-red-600">{error}</p>}

      {assessment && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-6" data-testid="assessment-pending">
          <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-amber-700">Order {assessment.reference} — SLE {assessment.amount.toLocaleString()} · Payment pending</p>
          <p className="mt-3 text-sm leading-relaxed text-slate-700">{assessment.instructions}</p>
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <button onClick={downloadPdf} disabled={downloading} data-testid="plan-download-pdf"
          className="inline-flex items-center gap-2 rounded-md bg-obsidian px-6 py-3.5 font-semibold text-white hover:bg-pine hover:text-volt transition-colors disabled:opacity-60">
          <Download className="h-4 w-4" aria-hidden="true" />
          {downloading ? "Preparing…" : "Download Preliminary Energy Report"}
        </button>
        <button onClick={() => setLeadMode("assessment")} data-testid="plan-request-assessment"
          className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-6 py-3.5 font-semibold text-slate-700 hover:border-emerald-400 transition-colors">
          Request Official BNM Engineering Assessment
        </button>
        <button onClick={() => setLeadMode("quote")} data-testid="plan-request-quote"
          className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-6 py-3.5 font-semibold text-slate-700 hover:border-emerald-400 transition-colors">
          Request BNM Quotation
        </button>
        <WhatsAppCTA message={waMessage} />
      </div>
      <div className="flex flex-wrap gap-3">
        <button onClick={savePlan} data-testid="plan-save"
          className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 hover:underline">
          <Save className="h-4 w-4" aria-hidden="true" /> Save to my dashboard
        </button>
        <button onClick={sendToSolarCalculator} data-testid="plan-to-solar"
          className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 hover:underline">
          Continue in Solar Calculator <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </button>
        {!assessment && (
          <button onClick={requestAssessment} data-testid="plan-paid-assessment"
            className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 hover:underline">
            <Lock className="h-4 w-4" aria-hidden="true" /> Get Your Detailed BNM Energy Assessment (paid)
          </button>
        )}
      </div>

      {leadMode && (
        <LeadForm
          type={leadMode}
          title={leadMode === "quote" ? "Request BNM Quotation" : "Request Official BNM Engineering Assessment"}
          context={summaryCtx}
          defaultMessage={`My plan: ${plan.energy.daily_kwh} kWh/day, peak ${plan.energy.peak_w} W — ${plan.solar.installed_kw} kW solar, ${plan.battery.installed_kwh} kWh battery, ${plan.inverter.recommended_kw} kW inverter.`}
        />
      )}
    </div>
  );
}
